import {
    Autocomplete,
    Box,
    Breadcrumbs,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    Link,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorMessage, Form, Formik, getIn } from "formik";
import * as Yup from "yup";
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from "../../MiniBot";
import MainDrawer from "../../MainDrawer";
import { useAuth } from "../../../helpers/AuthProvider";

// --- Interfaces and Constants ---
interface ConsumerGroupFormProps {
    isEdit: boolean;
}

const validationSchema = Yup.object().shape({
    name: Yup.string().required("Consumer Group Name is required"),
    description: Yup.string(),
    rate: Yup.string().matches(/^[0-9]*$/, "Rate must be a number").nullable(),
    burst: Yup.string().matches(/^[0-9]*$/, "Burst must be a number").nullable(),
    quotaRequests: Yup.string().matches(/^[0-9]*$/, "Quota requests must be a number").nullable(),
    quotaPeriod: Yup.string().nullable(),
    costCenterValue: Yup.string()
        .required("This field is required")
        .strict()
        .lowercase("This field must be in lowercase")
        .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
        .max(20, "This field must not exceed 20 characters"),
    costLabelPairs: Yup.array()
        .of(
            Yup.object({
                cost: Yup.string()
                    .required("This field is required")
                    .strict()
                    .lowercase("This field must be in lowercase")
                    .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
                    .max(20, "This field must not exceed 20 characters"),
                label: Yup.string()
                    .required("This field is required")
                    .strict()
                    .lowercase("This field must be in lowercase")
                    .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
                    .max(20, "This field must not exceed 20 characters"),
            })
        )
});

const quotaPeriodList = ["day", "week", "month"];

// --- Form Content Component ---
const ConsumerGroupFormContent: React.FC<ConsumerGroupFormProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [consumerGroup, setConsumerGroup] = useState<any>({});

    const { user } = useAuth();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;

    useEffect(() => {
        if (!isEdit || !id) return;
        const fetchConsumerGroupData = async () => {
            try {
                const data = await queryGraphql(`
                query GetConsumerGroup($getConsumerGroupId: String!) {
                  getConsumerGroup(id: $getConsumerGroupId) {
                    id
                    name
                    description
                    rate
                    burst
                    quotaRequests
                    quotaPeriod
                    status
                    costLabelPairs { cost, label }
                  }
                }`, { getConsumerGroupId: id });
                
                if (data.getConsumerGroup) {
                    setConsumerGroup(data.getConsumerGroup);
                }
            } catch (error) { console.error("Error fetching Consumer Group details:", error); }
        };
        fetchConsumerGroupData();
    }, [id, isEdit]);

    return (
        <Container disableGutters maxWidth={false} sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/consumer-group">Consumer Groups</Link>
                    <Typography color="text.primary">{isEdit ? "Edit Consumer Group" : "Add New Consumer Group"}</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={2}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/consumer-group")}>
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">{isEdit ? "Edit Consumer Group" : "Create New Consumer Group"}</Typography>
                </Box>
            </Box>
            <Grid container>
                <Grid item xs={1}></Grid>
                <Grid item xs={10}>
                    {((isEdit && consumerGroup.id) || !isEdit) && (
                        <Formik
                            initialValues={{
                                name: consumerGroup.name || "",
                                description: consumerGroup.description || "",
                                rate: consumerGroup.rate || "",
                                burst: consumerGroup.burst || "",
                                quotaRequests: consumerGroup.quotaRequests || "",
                                quotaPeriod: consumerGroup.quotaPeriod || null,
                                costCenterValue: consumerGroup.costLabelPairs?.find((elem:any) => elem.cost === "cost-center")?.label || "",
                                costLabelPairs: consumerGroup.costLabelPairs?.filter((e:any) => e.cost !== "cost-center").map((pair:any) => ({...pair, id: crypto.randomUUID()})) || [],
                            }}
                            validationSchema={validationSchema}
                            enableReinitialize
                            onSubmit={async (values) => {
                                const costLabelPairs = [
                                    { cost: "cost-center", label: values.costCenterValue },
                                    ...values.costLabelPairs.map(({ cost, label }: { cost: string, label: string }) => ({ cost, label }))
                                ];

                                const input = {
                                    name: values.name,
                                    description: values.description || null,
                                    rate: values.rate || null,
                                    burst: values.burst || null,
                                    quotaRequests: values.quotaRequests || null,
                                    quotaPeriod: values.quotaPeriod || null,
                                    costLabelPairs,
                                };

                                try {
                                    if (isEdit) {
                                        const updateInput = { ...input, id: id, status: consumerGroup.status };
                                        await queryGraphql(`mutation UpdateConsumerGroup($input: UpdateConsumerGroupInput!) { updateConsumerGroup(input: $input) { id } }`, { input: updateInput });
                                    } else {
                                        await queryGraphql(`mutation CreateConsumerGroup($input: CreateConsumerGroupInput!) { createConsumerGroup(input: $input) { id } }`, { input });
                                    }
                                    navigate("/consumer-group");
                                } catch (error) { console.error("Failed to save Consumer Group:", error); }
                            }}
                        >
                            {({ setFieldValue, errors, touched, values, handleChange, handleBlur, isValid, dirty }) => (
                                <Form id="main-form">
                                    <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={4}>
                                        {isEdit ? "Make changes to the details below and save" : "Fill the details below to add a Consumer Group"}
                                    </Typography>

                                    <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                        <Grid container spacing={4}>
                                            <Grid item xs={12} md={6}>
                                                <CompulsoryFieldsLabel fieldName="Consumer Group Name" />
                                                <TextField variant="filled" fullWidth name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Enter a unique name for the consumer group" hiddenLabel disabled={consumerGroup.status === 'active'}
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.name && touched.name ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="name" /></div>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <CompulsoryFieldsLabel fieldName="Description" />
                                                <TextField variant="filled" fullWidth multiline rows={1} name="description" value={values.description} onChange={handleChange} onBlur={handleBlur} placeholder="Provide a brief description of the consumer group" hiddenLabel
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.description && touched.description ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="description" /></div>
                                            </Grid>
                                        </Grid>
                                    </div>

                                    <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                        <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
                                            Throttling & Quota Settings
                                        </Typography>
                                        <Divider sx={{ marginBottom: "24px" }} />
                                        <Grid container spacing={4}>
                                            <Grid item xs={12} md={6}>
                                                <CompulsoryFieldsLabel fieldName="Rate Limit (requests/second)"  />
                                                <TextField variant="filled" fullWidth name="rate" value={values.rate} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., 100" hiddenLabel disabled={consumerGroup.status === 'active'}
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.rate && touched.rate ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="rate" /></div>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <CompulsoryFieldsLabel fieldName="Burst Limit (requests/second)"  />
                                                <TextField variant="filled" fullWidth name="burst" value={values.burst} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., 200" hiddenLabel disabled={consumerGroup.status === 'active'}
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.burst && touched.burst ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="burst" /></div>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <CompulsoryFieldsLabel fieldName="Quota (requests)"  />
                                                <TextField variant="filled" fullWidth name="quotaRequests" value={values.quotaRequests} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., 5000" hiddenLabel disabled={consumerGroup.status === 'active'}
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.quotaRequests && touched.quotaRequests ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="quotaRequests" /></div>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <CompulsoryFieldsLabel fieldName="Quota Period"  />
                                                <Autocomplete options={quotaPeriodList}
                                                    value={values.quotaPeriod}
                                                    onChange={(_, newValue) => setFieldValue("quotaPeriod", newValue || null)}
                                                    renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="Select Quota Period" hiddenLabel disabled={consumerGroup.status === 'active'}
                                                        sx={{ "& .MuiFilledInput-root": { border: errors.quotaPeriod && touched.quotaPeriod ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                        InputProps={{ ...params.InputProps, disableUnderline: true, style: { borderRadius: "8px" } }} />)}
                                                />
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="quotaPeriod" /></div>
                                            </Grid>
                                        </Grid>
                                    </div>

                                    <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                        <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
                                            Tags / Labels
                                        </Typography>
                                        <Divider sx={{ marginBottom: "24px" }} />
                                        <Grid container spacing={4} style={{ marginBottom: "16px" }}>
                                            <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography></Grid>
                                            <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Value</Typography></Grid>
                                        </Grid>
                                        <Grid container spacing={4}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth disabled value="cost-center" variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth placeholder="Enter value" value={values.costCenterValue} hiddenLabel variant="filled" name="costCenterValue" disabled={consumerGroup.status === 'active'}
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.costCenterValue && touched.costCenterValue ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    onChange={handleChange} onBlur={handleBlur}
                                                    InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" component="div" /></div>
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={4} mt="8px">
                                            {values.costLabelPairs?.map((pair:any, index:any) => (
                                                <React.Fragment key={pair.id}>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField fullWidth placeholder="Enter key" value={pair.cost} hiddenLabel variant="filled" disabled={consumerGroup.status === 'active'}
                                                            sx={{ "& .MuiFilledInput-root": { border: getIn(errors, `costLabelPairs[${index}].cost`) && getIn(touched, `costLabelPairs[${index}].cost`) ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                            onChange={(event) => setFieldValue(`costLabelPairs[${index}].cost`, event.target.value)}
                                                            InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name={`costLabelPairs[${index}].cost`} component="div" /></div>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Box display="flex" alignItems="center">
                                                            <TextField fullWidth placeholder="Enter value" value={pair.label} hiddenLabel variant="filled" disabled={consumerGroup.status === 'active'}
                                                                sx={{ "& .MuiFilledInput-root": { border: getIn(errors, `costLabelPairs[${index}].label`) && getIn(touched, `costLabelPairs[${index}].label`) ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                                onChange={(event) => setFieldValue(`costLabelPairs[${index}].label`, event.target.value)}
                                                                InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                            <IconButton sx={{ marginLeft: "8px" }} disabled={consumerGroup.status === 'active'} onClick={() => {
                                                                const newPairs = values.costLabelPairs.filter((_:any, i:any) => i !== index);
                                                                setFieldValue("costLabelPairs", newPairs);
                                                            }}>
                                                                <img alt="close-icon" src="/assets/close-icon.svg" />
                                                            </IconButton>
                                                        </Box>
                                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name={`costLabelPairs[${index}].label`} component="div" /></div>
                                                    </Grid>
                                                </React.Fragment>
                                            ))}
                                        </Grid>
                                        <Button variant="text" sx={{ color: "#000000", textTransform: "none", mt: "16px" }} disabled={consumerGroup.status === 'active'} onClick={() => setFieldValue("costLabelPairs", [...values.costLabelPairs, { cost: "", label: "", id: crypto.randomUUID() }])}>
                                            + Add Key Value Pair
                                        </Button>
                                    </div>

                                    <Box display="flex" justifyContent="flex-end" gap={2}>
                                        <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4 }} variant="outlined" size="large" onClick={() => navigate("/consumer-group")}>Cancel</Button>
                                        <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "white", color: "black", borderColor: "black" }, textTransform: "none", px: 4, }} variant="contained" size="large" type="submit" disabled={!isValid || !dirty || isViewer}>
                                            {isEdit ? "Save Changes" : "Create Consumer Group"}
                                        </Button>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    )}
                </Grid>
                <Grid item xs={1}></Grid>
            </Grid>
        </Container>
    );
};

// --- Main Export Component ---
const ConsumerGroupForm: React.FC<ConsumerGroupFormProps> = ({ isEdit }) => {
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"apis-consumer-group"} />
            <Box component="main" sx={{ flexGrow: 1 }}>
                <ConsumerGroupFormContent isEdit={isEdit} />
            </Box>
        </Box>
    );
};

export default ConsumerGroupForm;